import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Download, FileSpreadsheet, Upload, Package, ChefHat, ClipboardList, AlertTriangle, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useUnit } from "@/context/UnitContext";
import { Button, Card, Page, PageHeader, Segmented } from "@/components/kit";

const PARTS = [
  { id: "products", label: "Produtos", hint: "com categorias, setores e histórico de custos", icon: Package },
  { id: "recipes", label: "Fichas técnicas", hint: "pratos e preparos base", icon: ChefHat },
  { id: "conferences", label: "Conferências", hint: "com compras, notas, perdas e vendas importadas", icon: ClipboardList },
];
const ALL = PARTS.map((p) => p.id);

async function download(path, fallback) {
  const blob = await api(path, { responseType: "blob" });
  const url = URL.createObjectURL(blob);
  Object.assign(document.createElement("a"), { href: url, download: fallback }).click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

function PartPicker({ value, onChange, only, prefix }) {
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div className="flex flex-col gap-2">
      {PARTS.filter((p) => !only || only.includes(p.id)).map((p) => {
        const on = value.includes(p.id);
        return (
          <button key={p.id} type="button" onClick={() => toggle(p.id)} data-testid={`${prefix}-part-${p.id}`}
            className="flex items-center gap-3 rounded-xl p-3 text-left border transition-colors"
            style={{ borderColor: on ? "var(--brand)" : "var(--border)", background: on ? "var(--brand-soft)" : "var(--surface)" }}>
            <span className="w-5 h-5 rounded-md flex items-center justify-center border" style={{ borderColor: on ? "var(--brand)" : "var(--border)", background: on ? "var(--brand)" : "transparent" }}>{on && <Check size={13} color="#fff" />}</span>
            <p.icon size={17} style={{ color: "var(--brand)" }} />
            <span className="flex-1"><span className="label">{p.label}</span><span className="caption block">{p.hint}</span></span>
          </button>
        );
      })}
    </div>
  );
}

function ExportCard({ unitName }) {
  const [parts, setParts] = useState(ALL);
  const [busy, setBusy] = useState(null);
  const run = (kind) => async () => {
    setBusy(kind);
    const day = new Date().toISOString().slice(0, 10);
    const slug = unitName.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 30) || "unidade";
    try {
      await download(`/backup/${kind === "json" ? "export" : "export.xlsx"}?sections=${parts.join(",")}`, `backup-${slug}-${day}.${kind}`);
      toast.success(kind === "json" ? "Backup baixado" : "Planilha baixada");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };
  return (
    <Card className="flex flex-col gap-4" data-testid="backup-export-card">
      <div><div className="h3">Exportar</div><p className="caption">Dados da unidade <b>{unitName}</b>. O arquivo de backup restaura tudo; a planilha serve para consultar no Excel.</p></div>
      <PartPicker value={parts} onChange={setParts} prefix="export" />
      <div className="flex flex-wrap gap-2">
        <Button icon={Download} disabled={!parts.length} loading={busy === "json"} onClick={run("json")} data-testid="backup-export-json-button">Baixar backup completo</Button>
        <Button variant="secondary" icon={FileSpreadsheet} disabled={!parts.length} loading={busy === "xlsx"} onClick={run("xlsx")} data-testid="backup-export-xlsx-button">Baixar planilha Excel</Button>
      </div>
    </Card>
  );
}

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "-");

function PreviewInfo({ p }) {
  const c = p.counts;
  return (
    <div className="rounded-xl p-3 flex flex-col gap-1" style={{ background: "var(--surface-alt)" }} data-testid="backup-preview">
      <div className="label">Backup de “{p.unit_name}” · {fmt(p.exported_at)}{p.exported_by ? ` · por ${p.exported_by}` : ""}</div>
      <div className="caption tnum" data-testid="backup-preview-counts">{c.products} produtos · {c.categories} categorias · {c.recipes} fichas técnicas · {c.conferences} conferências · {c.invoices} notas · {c.losses} perdas</div>
    </div>
  );
}

function ImportCard({ unitName }) {
  const qc = useQueryClient();
  const ref = useRef();
  const [file, setFile] = useState(null);
  const [p, setP] = useState(null);
  const [parts, setParts] = useState([]);
  const [mode, setMode] = useState("merge");
  const [busy, setBusy] = useState(false);
  const reset = () => { setFile(null); setP(null); if (ref.current) ref.current.value = ""; };
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const form = new FormData();
    form.append("file", f);
    setBusy(true);
    try {
      const r = await api("/backup/preview", { method: "POST", form });
      setFile(f); setP(r); setParts(r.sections); setMode("merge");
    } catch (err) {
      toast.error(err.message); reset();
    } finally {
      setBusy(false);
    }
  };
  const go = async () => {
    if (mode === "replace" && !window.confirm(`Substituir ${parts.map((x) => PARTS.find((y) => y.id === x).label.toLowerCase()).join(", ")} da unidade ${unitName}? Os dados atuais dessas partes serão apagados.`)) return;
    const form = new FormData();
    form.append("file", file); form.append("mode", mode); form.append("sections", parts.join(","));
    setBusy(true);
    try {
      const r = await api("/backup/import", { method: "POST", form });
      qc.invalidateQueries();
      toast.success(`Backup importado · agora a unidade tem ${r.current.products} produtos, ${r.current.recipes} fichas e ${r.current.conferences} conferências`);
      if (r.codes_cleared) toast.warning(`${r.codes_cleared} código(s) PDV repetido(s) foram limpos para evitar conflito`);
      reset();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="flex flex-col gap-4" data-testid="backup-import-card">
      <div><div className="h3">Importar</div><p className="caption">Envie um arquivo .json gerado por “Baixar backup completo”. Os dados entram na unidade <b>{unitName}</b>.</p></div>
      <input ref={ref} type="file" accept=".json,application/json" className="hidden" onChange={pick} data-testid="backup-file-input" />
      {!p ? <Button variant="secondary" icon={Upload} loading={busy} onClick={() => ref.current?.click()} className="self-start" data-testid="backup-pick-file-button">Escolher arquivo de backup</Button> : (
        <>
          <PreviewInfo p={p} />
          <div className="label">O que importar</div>
          <PartPicker value={parts} onChange={setParts} only={p.sections} prefix="import" />
          <div className="label">O que fazer com os dados atuais</div>
          <Segmented options={[{ value: "merge", label: "Juntar com os atuais" }, { value: "replace", label: "Substituir tudo" }]} value={mode} onChange={setMode} testid="backup-mode" />
          <p className="caption flex gap-2" data-testid="backup-mode-note" style={mode === "replace" ? { color: "var(--danger)" } : undefined}>
            {mode === "replace" && <AlertTriangle size={15} className="shrink-0 mt-0.5" />}
            {mode === "merge" ? "Atualiza o que já existe com os dados do backup e adiciona o que falta. Nada é apagado." : `Apaga as partes escolhidas da unidade (hoje: ${p.current.products} produtos, ${p.current.recipes} fichas, ${p.current.conferences} conferências) e coloca o conteúdo do backup no lugar. Baixe um backup atual antes, por segurança.`}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button icon={Upload} variant={mode === "replace" ? "danger" : "primary"} loading={busy} disabled={!parts.length} onClick={go} data-testid="backup-import-button">{mode === "replace" ? "Substituir e importar" : "Importar"}</Button>
            <Button variant="ghost" onClick={reset} data-testid="backup-cancel-button">Cancelar</Button>
          </div>
        </>
      )}
    </Card>
  );
}

export default function Backup() {
  const { unit } = useUnit();
  const name = unit?.name || "selecionada";
  return (
    <Page narrow>
      <PageHeader title="Backup dos dados" subtitle="Exporte e restaure produtos, fichas técnicas e conferências" back="/menu" />
      <div className="flex flex-col gap-5">
        <ExportCard unitName={name} />
        <ImportCard unitName={name} />
        <p className="caption">As fotos (notas, fichas e tarefas) não vão dentro do arquivo · elas continuam aparecendo enquanto estiverem guardadas neste sistema.</p>
      </div>
    </Page>
  );
}
